import axios from 'axios'

import formTypes from '@/constants/form'
import routes from '@/internal/constants/routes'
import { type FormWebsite } from '@/internal/types/form'
import { buildUTMTrackingUrl, getStoredUTMParams } from '@/utils/analytics'

interface MatomoTracker {
  getUserId(): string
}

export const matomoTrackForm = (downloads, formName, formId): void => {
  if (!window?._paq || !downloads?.length) {
    console.log('⚠️ Matomo not available or no downloads')
    return
  }

  if (window?._paq) {
    const baseUrl = window.location.href
    const utmParams = getStoredUTMParams()
    downloads.forEach((download, index) => {
      if (window?._paq) {
        // Trackear evento
        window._paq.push([
          'trackEvent',
          'Paper',
          'Downloaded',
          download.externalName,
          {
            dimension1: download.originalId,
            dimension2: utmParams.source || 'direct',
            dimension3: utmParams.content,
            dimension4: download.externalName,
            dimension5: (index + 1).toString(),
            dimension6: utmParams.campaign,
            dimension7: utmParams.placement,
            dimension8: 'client_primary'
          }
        ])
      }
    })

    // @see https://developer.matomo.org/guides/form-analytics/reference
    // Track conversion.
    window._paq.push(['FormAnalytics::trackFormConversion', formName, formId])
  }
}

/**
 * Obtiene el userID de Matomo desde el frontend
 * @returns Promise que resuelve con el UserID o null si no está disponible
 */
export const getMatomoUserId = (): string | null => {
  if (typeof window === 'undefined' || !window._paq) {
    return null
  }

  let userId: string | null = null

  try {
    window._paq.push([
      function (this: MatomoTracker) {
        userId = this.getUserId() || null
      }
    ])
  } catch (error) {
    console.error('Error getting User ID:', error)
  }

  return userId
}

type ResponseForm = {
  error: boolean
  message: string
  status: number
  data: FormWebsite
}

export const submitForm = async ({
  data,
  save = false
}: {
  data: FormWebsite
  save?: boolean
}): Promise<FormWebsite> => {
  try {
    const storedParams = getStoredUTMParams()
    const route =
      data.form === 'bulletin'
        ? save
          ? routes.apiSubscriptionSaveRoute
          : routes.apiSubscriptionRoute
        : save
          ? routes.apiFormSaveRoute
          : routes.apiFormRoute

    const response = await axios.post<ResponseForm>(route, {
      ...data,
      campaign: storedParams.campaign || '',
      utmSource: storedParams.source || '',
      utmMedium: storedParams.medium || '',
      utmContent: storedParams.content || '',
      mtmPlacement: storedParams.placement || ''
    })

    if (response.data.error) {
      throw new Error(response.data.message)
    }
    return response.data?.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      console.log(error)
      throw new Error(
        (error?.response?.data as ResponseForm)?.message ??
          'Error submitting form'
      )
    } else if (error instanceof Error) {
      throw new Error(error.message)
    } else {
      throw new Error('Fatal error')
    }
  }
}

export function isInvalidForm(values: FormWebsite): boolean {
  if (values === undefined) {
    return true
  }
  const { form } = values
  return form === undefined || !Object.values(formTypes).includes(form)
}

export function isFormGate(values: FormWebsite): boolean {
  return ['gate', 'paperDetail'].includes(values?.form)
}

export function isEmptyRecordIds(values: FormWebsite): boolean {
  if (values?.form === 'bulletin') {
    return true
  }
  const { downloads } = values
  if (downloads === undefined) {
    return true
  }
  return downloads.length === 0
}

export function hasVariousRecordsIds(values: FormWebsite): boolean {
  return (
    values.form !== 'bulletin' &&
    Array.isArray(values.downloads) &&
    values.downloads.length > 1
  )
}
